import { SCORING_METHOD } from "@/defines/scoringmethod";
import type Card from "./card";
import Chair from "./chair";
import Hand from "./hand";
import History, { HistoryFlag } from "./history";
import Vulnerability from "./vulnerability";

export enum PLAYING_MODE {
    NONE = 0,
    RECREATIONAL = 1,
    COMPETITION = 2,
    COURSE = 5,
}



export class BoardDTO {
    id: number = -1;
    number: number = 0;
    isFinished = false;
    showCompetitiveScores = false;
    scoringMethod = SCORING_METHOD.NONE;
    vulnerability = new Vulnerability();
    dealer = new Chair();
    datumScore = 0;

    defaultHistoryId = -1;

    hand: Hand[] = new Array(4);
    perspective = new Chair().setSouth();

    fetchTime = Date.now(); // time we got this board from the server, so we can sort on it.

    // Set when a detail fetch (historyResult) changed a pair's score/competitive
    // score, which invalidates the whole field's relative scores. Persisted so
    // the field list — possibly shown in a separate Electron window — can pick it
    // up via the Dexie liveQuery and refresh from the server. Cleared by boardResult().
    fieldDirty = false;
}

export default class Board extends BoardDTO {

    historyMap = new Map<number, History>();

    get history(): History {
        let h = this.historyMap.get(this.defaultHistoryId);
        if (h === undefined) {
            h = new History(this);
            this.historyMap.set(h.id, h);
        }
        return h;
    }

    constructor() {
        super();

        for (let i = 0; i < 4; i++) {
            this.hand[i] = new Hand(i);
        }
    }

    static fromObject(o: any) {
        const b = new Board();
        b.id = o.id ?? -1;
        b.number = o.number ?? 0;
        b.isFinished = o.isFinished ?? b.isFinished;
        b.showCompetitiveScores = o.showCompetitiveScores ?? b.showCompetitiveScores;
        b.scoringMethod = o.scoringMethod ?? b.scoringMethod;
        b.vulnerability.set(o.vulnerability);
        b.dealer.set(o.dealer);
        b.datumScore = o.datumScore ?? 0;
        b.fetchTime = o.fetchTime ?? Date.now();
        b.defaultHistoryId = o.defaultHistoryId ?? -1;
        b.fieldDirty = o.fieldDirty ?? false;

        b.hand = [];
        for (const h of o.hand) {
            b.hand.push(Hand.fromObject(h));
        }

        b.perspective.set(o.perspective);
        return b;
    }

    // creates a new history in cache
    newHistory(hisId: number) {
        // erase any previous history with same id, except when it's currently in play
        this.historyMap.delete(hisId);
        // create new one
        const his = new History(this);
        his.id = hisId;
        this.historyMap.set(hisId, his);

        return his;
    }

    addHistory(history: History) {
        this.historyMap.set(history.id, history);
        if (this.defaultHistoryId === -1 && this.historyMap.size === 1) {
            this.defaultHistoryId = history.id;
        }
    }

    isVulnerable(chair: Chair) {
        return this.vulnerability.isVulnerable(chair);
    }

    getPerspective(player: string): Chair {
        // first return the chair of the player from the default history
        if (this.history) {
            for (let i = 0; i < 4; i++) {
                if (player == this.history.playersName[i]) {
                    return new Chair().setInt(i);
                }
            }
        }
        // if not found, then we use the default perspective.
        return this.perspective;
    }

    setPerspective(chair: Chair) {
        this.perspective.set(chair);
    }


    /**
     * gets the history which is played at a closed table during a vugraphed team match
     */
    getClosedTableHistory() {
        for (const h of this.historyMap.values()) {
            if (h.id !== -1 && h.flag === HistoryFlag.CLOSED_TABLE_RESULT) {
                return h;
            }
        }
        return undefined;
    }

    /**
     * gets the first history which is played at an open table during a vugraphed team match 
     */
    getOpenTableHistory() {
        for (const h of this.historyMap.values()) {
            if (h.id !== -1 && h.flag !== HistoryFlag.CLOSED_TABLE_RESULT) {
                return h;
            }
        }
        return undefined;
    }

    /**
       * Gets and returns the Card object which belongs to the integer value of int.
       * @param int the integer value of the Card object we look for
       * @return the Card object belonging to the value of int
       */
    getCardByInt(int: number): Card | undefined {
        for (let h of this.hand) {
            for (let card of h.cards) {
                if (card.getInt() === int) {
                    return card;
                }
            }
        }
        return undefined;
    }

    /**
     * Gets and returns the Card object of this board which has the same value as the card
     * provided or an invalid one which is not yet played.
     * @param card value of the Card object we look for
     * @return the Card object belonging to this board with the same value
     */
    getCardToPlay(card: Card, chair: Chair) {
        if (chair.isPlayer()) {
            const hand = this.hand[chair.getInt()];

            for (const c of hand.cards) {
                if (c.getInt() === card.getInt()) {
                    return c;
                }
            }
            // we didn't find it, so we make one of the unknown cards this card
            const c = hand.cards.find((c) => { return !c.isValid() && !c.played; });

            if (c) {
                c.setInt(card.getInt());
            }
            return c;
        }
        return undefined;
    }

    /**
     * Gets and returns the Card object of this board which has the same value as the card
     * provided, or an invalid one which was already played.
     * @param card value of the Card object we look for
     * @return the Card object belonging to this board with the same value
     */
    getCardToUndo(card: Card, chair: Chair) {
        if (chair.isPlayer()) {
            const hand = this.hand[chair.getInt()];

            for (const c of hand.cards) {
                if (c.getInt() === card.getInt()) {
                    return c;
                }
            }
            // we didn't find it, so we make one of the unknown cards this card
            const c = hand.cards.find((c) => { return !c.isValid() && c.played; });

            if (c) {
                c.setInt(card.getInt());
            }
            return c;
        }
        return undefined;
    }

    setDeck(deck: string) {
        let i = 0;

        for (let hand of this.hand) {
            if (hand === undefined) {
                console.error('setDeck() no hand in', this.hand);
                continue;
            }
            // add card if needed.
            hand.createCards();

            for (let card of hand.cards) {
                let int = deck.charCodeAt(i++) - 'A'.charCodeAt(0);
                card.setInt(int);
                card.played = false;
            }
        }
    }

    setDeckOfChair(deck: string, chair: Chair) {
        const i = chair.getInt();
        const hand = this.hand[i];

        if (hand === undefined) {
            console.error('setDeckOfChar() no hand with index ' + i);
            return;
        }

        let deckchars = "";
        let playedchars = "";

        switch (deck.length) {
            case 52: // only take the 13 cards for the chair
                deckchars = deck.slice(13 * i, 13 * i + 13);
                break;
            case 26: // includes isplayed information like "CGHJLNOXY^`gj0000000000000"
                deckchars = deck.slice(0, 13);
                playedchars = deck.slice(13, 26);
                break;
            case 13:
                deckchars = deck;
                break;
            default:
                console.error("Board.setDeck(const char *, const Chair &), Deck string length must be 13 or 52!");
                return;
        }

        hand.createCards();

        const withPlayedInfo = playedchars.length > 0;

        for (let k = 0; k < 13; k++) {
            const int = deckchars.charCodeAt(k) - 'A'.charCodeAt(0);
            hand.cards[k].setInt(int);

            const played = withPlayedInfo ? playedchars.charAt(k) == '1' : false;
            hand.cards[k].played = played;
        }
    }
}
